import React, { useState, useEffect } from "react";
import { Container } from "./styles";
import {
  FiFileText,
  FiClock,
  FiCheckCircle,
  FiSearch,
  FiCalendar,
  FiHash,
} from "react-icons/fi";
import {
  HiOutlineCheckCircle,
  HiOutlineXCircle,
} from "react-icons/hi";
import { GoOrganization } from "react-icons/go";
import api from "../../services/api";
import DecModal from "./DecModal";
import DecLoadingSkeleton from "./DecLoadingSkeleton";
import ConfirmModal from "../../components/ConfirmModal";

const STATUS_CONFIG = {
  nao_resolvido: { label: 'Não Resolvido', color: '#f87171', bg: 'rgba(239, 68, 68, 0.12)', border: 'rgba(239, 68, 68, 0.3)', key: 'danger', icon: HiOutlineXCircle },
  em_analise:    { label: 'Em Análise',    color: '#fbbf24', bg: 'rgba(251, 191, 36, 0.12)', border: 'rgba(251, 191, 36, 0.3)', key: 'warn',   icon: FiClock },
  resolvido:     { label: 'Resolvido',     color: '#34d399', bg: 'rgba(16, 185, 129, 0.12)', border: 'rgba(16, 185, 129, 0.3)', key: 'ok',     icon: HiOutlineCheckCircle },
};

export default function DEC({ embedded = false }) {
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [notificacoes, setNotificacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({ total: 0, pendentes: 0, emAnalise: 0, resolvidas: 0, importantes: 0, esteMes: 0, naoLidas: 0 });
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [statusModal, setStatusModal] = useState({ open: false, notificacao: null, newStatus: null });

  useEffect(() => { fetchNotificacoes(); fetchKpis(); }, []);

  const fetchNotificacoes = async () => {
    try {
      const projeto = localStorage.getItem('vidal_selected_project');
      const response = await api.get("/dec/notificacoes", { params: { projeto, limit: 100 } });
      if (response.data.success) setNotificacoes(response.data.notificacoes || []);
    } catch (error) { console.error("Erro ao buscar notificações DEC:", error); }
    finally { setLoading(false); }
  };

  const fetchKpis = async () => {
    try {
      const projeto = localStorage.getItem('vidal_selected_project');
      const response = await api.get("/dec/kpis", { params: { projeto } });
      if (response.data.success) setKpis(response.data.kpis || { total: 0, pendentes: 0, emAnalise: 0, resolvidas: 0, importantes: 0, esteMes: 0, naoLidas: 0 });
    } catch (error) { console.error("Erro ao buscar KPIs DEC:", error); }
  };

  const handleViewDetails = async (notificacao) => {
    setModalOpen(true);
    setLoadingDetails(true);
    try {
      const encodedId = encodeURIComponent(notificacao.id);
      const response = await api.get(`/dec/notificacoes/${encodedId}`);
      if (response.data.success) setSelectedNotification(response.data.notificacao);
    } catch (error) { console.error("Erro ao buscar detalhes:", error); setSelectedNotification(notificacao); }
    finally { setLoadingDetails(false); }
  };

  const handleCloseModal = () => { setModalOpen(false); setSelectedNotification(null); };

  const handleStatusClick = (e, notificacao) => {
    e.stopPropagation();
    const currentStatus = notificacao.status || 'nao_resolvido';
    const statusOrder = ['nao_resolvido', 'em_analise', 'resolvido'];
    const nextStatus = statusOrder[(statusOrder.indexOf(currentStatus) + 1) % statusOrder.length];
    setStatusModal({ open: true, notificacao, newStatus: nextStatus });
  };

  const handleConfirmStatus = async () => {
    const { notificacao, newStatus } = statusModal;
    if (!notificacao || !newStatus) return;
    try {
      const encodedId = encodeURIComponent(notificacao.id);
      await api.patch(`/dec/status/${encodedId}`, { status: newStatus });
      setNotificacoes(prev => prev.map(n => n.id === notificacao.id ? { ...n, status: newStatus, resolvida: newStatus === 'resolvido' } : n));
      if (selectedNotification && selectedNotification.id === notificacao.id) {
        setSelectedNotification(prev => ({ ...prev, status: newStatus, resolvida: newStatus === 'resolvido' }));
      }
      fetchKpis();
    } catch (error) { console.error("Erro ao atualizar status:", error); }
    finally { setStatusModal({ open: false, notificacao: null, newStatus: null }); }
  };

  const handleUpdateStatus = async (updates) => {
    if (!selectedNotification) return;
    try {
      const encodedId = encodeURIComponent(selectedNotification.id);
      await api.patch(`/dec/status/${encodedId}`, updates);
      setSelectedNotification(prev => ({ ...prev, ...updates }));
      setNotificacoes(prev => prev.map(n => n.id === selectedNotification.id ? { ...n, ...updates } : n));
      fetchKpis();
    } catch (error) { console.error("Erro ao atualizar status:", error); }
  };

  const getStatusInfo = (notificacao) => {
    const status = notificacao.status || 'nao_resolvido';
    return STATUS_CONFIG[status] || STATUS_CONFIG.nao_resolvido;
  };

  const filteredNotifications = notificacoes.filter((notificacao) => {
    const status = notificacao.status || 'nao_resolvido';
    const matchesFilter = filter === "all" || (filter === "pending" && status === 'nao_resolvido') || (filter === "em_analise" && status === 'em_analise') || (filter === "resolved" && status === 'resolvido');
    const matchesSearch = !searchTerm || notificacao.assunto?.toLowerCase().includes(searchTerm.toLowerCase()) || notificacao.identificacao?.toLowerCase().includes(searchTerm.toLowerCase()) || notificacao.cnpj?.includes(searchTerm) || notificacao.conteudoTexto?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    const isDark = document.body.classList.contains('dark-theme') || document.documentElement.getAttribute('data-theme') === 'dark' || window.matchMedia('(prefers-color-scheme: dark)').matches;
    return <DecLoadingSkeleton isDark={isDark} />;
  }

  return (
    <Container>
      {!embedded && (
        <div className="header-info">
          <div className="header-details">
            <h3>DEC - Domicílio Eletrônico do Contribuinte</h3>
            <div className="header-meta">
              <span className="status-tag">Sistema Ativo</span>
              <span className="update-tag">{kpis.total} notificações monitoradas</span>
            </div>
          </div>
        </div>
      )}

      <div className="kpi-cards">
        <div className="kpi-card">
          <div className="card-content">
            <div className="thumb"><FiFileText /></div>
            <div className="text">
              <p>{kpis.total}</p>
              <p>Total</p>
            </div>
          </div>
        </div>
        <div className="kpi-card danger">
          <div className="card-content">
            <div className="thumb"><HiOutlineXCircle /></div>
            <div className="text">
              <p>{kpis.pendentes}</p>
              <p>Não Resolvidas</p>
            </div>
          </div>
        </div>
        <div className="kpi-card warning">
          <div className="card-content">
            <div className="thumb"><FiClock /></div>
            <div className="text">
              <p>{kpis.emAnalise || 0}</p>
              <p>Em Análise</p>
            </div>
          </div>
        </div>
        <div className="kpi-card success">
          <div className="card-content">
            <div className="thumb"><HiOutlineCheckCircle /></div>
            <div className="text">
              <p>{kpis.resolvidas}</p>
              <p>Resolvidas</p>
            </div>
          </div>
        </div>
      </div>

      <div className="toolbar">
        <div className="search-box">
          <FiSearch />
          <input type="text" placeholder="Buscar notificações..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="toolbar-right">
          <button className={`filter-pill ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>Todas</button>
          <button className={`filter-pill ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')}>Não Resolvidas</button>
          <button className={`filter-pill ${filter === 'em_analise' ? 'active' : ''}`} onClick={() => setFilter('em_analise')}>Em Análise</button>
          <button className={`filter-pill ${filter === 'resolved' ? 'active' : ''}`} onClick={() => setFilter('resolved')}>Resolvidas</button>
        </div>
      </div>

      <div className="notif-grid">
        {filteredNotifications.length > 0 ? (
          filteredNotifications.map((notificacao) => {
            const statusInfo = getStatusInfo(notificacao);
            const StatusIcon = statusInfo.icon;
            return (
              <div
                key={notificacao.id}
                className={`notif-card ${statusInfo.key}`}
                onClick={() => handleViewDetails(notificacao)}
                style={{ opacity: notificacao.status === 'resolvido' ? 0.7 : 1 }}
              >
                <div className="notif-icon" style={{ background: statusInfo.bg, border: `1px solid ${statusInfo.border}` }}>
                  <StatusIcon style={{ color: statusInfo.color }} />
                </div>
                <div className="notif-info">
                  <h4 className="notif-title">{notificacao.assunto || 'Sem assunto'}</h4>
                  <div className="notif-meta">
                    <span><FiHash /> {notificacao.identificacao}</span>
                    <span><GoOrganization /> {notificacao.cnpj}</span>
                    <span><FiCalendar /> {notificacao.data || (notificacao.dataExtracao ? new Date(notificacao.dataExtracao).toLocaleDateString('pt-BR') : '-')}</span>
                  </div>
                  <div className="notif-tags">
                    <span className={`status-pill ${statusInfo.key}`}>{statusInfo.label}</span>
                    {notificacao.prazo && (
                      <span className="status-pill warn">Prazo: {notificacao.prazo}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="empty-state">
            <div className="empty-icon"><FiCheckCircle /></div>
            <h4>Nenhuma notificação encontrada</h4>
            <p>Todas as notificações do DEC estão em dia ou os filtros aplicados não retornaram resultados.</p>
          </div>
        )}
      </div>

      {modalOpen && (
        <DecModal notification={selectedNotification} loading={loadingDetails} onClose={handleCloseModal} onUpdateStatus={handleUpdateStatus} />
      )}

      <ConfirmModal
        isOpen={statusModal.open}
        onClose={() => setStatusModal({ open: false, notificacao: null, newStatus: null })}
        onConfirm={handleConfirmStatus}
        title="Alterar Status"
        message={`Deseja alterar o status desta notificação para "${statusModal.newStatus ? STATUS_CONFIG[statusModal.newStatus]?.label : ''}"?`}
        warning="Esta ação irá atualizar o status da notificação no sistema."
        variant="warning"
        confirmText="Confirmar"
        cancelText="Cancelar"
        confirmVariant="primary"
      />
    </Container>
  );
}
